import { PropsWithChildren, createElement, useMemo } from 'react';

import { PaymentProvider } from 'features/payment';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { ConfettiFriesAnimationProvider } from 'components/lottie-animations/confetti-fries';
import { ToasterProvider } from 'components/toaster/toaster-provider';
import { AttPermissionProvider } from 'state/app-tracking-transparency-permissions';
import { AuthProvider } from 'state/auth';
import { BrazeProvider } from 'state/braze';
import { ErrorsProvider } from 'state/errors';
import { ExperimentsProvider } from 'state/experiments';
import { FavoritesProvider } from 'state/favorites';
import { FlyBuyProvider } from 'state/flybuy';
import { GeolocationProvider } from 'state/geolocation';
import { GlobalStateProvider } from 'state/global-state/provider';
import { GraphQLProvider } from 'state/graphql';
import { HelmetProvider } from 'state/helmet';
import { IntlProvider } from 'state/intl';
import { LDProvider } from 'state/launchdarkly';
import { LocationProvider } from 'state/location';
import { LoyaltyProvider } from 'state/loyalty';
import { MenuProvider } from 'state/menu';
import { NetworkProvider } from 'state/network';
import { OnboardingProvider } from 'state/onboarding';
import { OrderProvider } from 'state/order';
// import { PaymentProvider as PaymentProviderV2 } from 'state/payments';
import { PushPermissionProvider } from 'state/push-permissions';
import { ScrollProvider } from 'state/scroll';
import { ServiceModeProvider } from 'state/service-mode';
import { PageManagerProvider } from 'state/static-page-manager';
import { StoreProvider } from 'state/store';
import { UIProvider } from 'state/ui';
import { UnifiedMenuProvider } from 'state/unified-menu';
import { theme } from 'styles/theme';
import { HideSplashScreen } from 'utils/hide-splash-screen';

import { UclProvider } from './ucl';

const themeConfig = { theme };

/** IMPORTANT: providers here should:
 * - useMemoAll on result to avoid re-renders
 * - NOT conditionally wrap children or return children or this will cause re-renders/remounts of the app/router causing subtle bugs
 */
const useProviders = () => {
  const UclProviderWithTheme = useMemo(
    () => ({ children }: PropsWithChildren<{}>) =>
      createElement(UclProvider, themeConfig, children),
    []
  );

  return [
    SafeAreaProvider,
    KeyboardProvider,
    UclProviderWithTheme,
    GlobalStateProvider,
    LDProvider,
    IntlProvider,
    LocationProvider,
    GraphQLProvider,
    BrazeProvider,
    ExperimentsProvider,
    ErrorsProvider,
    NetworkProvider,
    AuthProvider,
    UIProvider,
    ToasterProvider,
    OnboardingProvider,
    ServiceModeProvider,
    StoreProvider,
    LoyaltyProvider,
    UnifiedMenuProvider,
    MenuProvider,
    PaymentProvider,
    PageManagerProvider,
    PushPermissionProvider,
    OrderProvider,
    FavoritesProvider,
    GeolocationProvider,
    HelmetProvider,
    ScrollProvider,
    FlyBuyProvider,
    ConfettiFriesAnimationProvider,
    AttPermissionProvider,

    // "clever" way to hide the splash screen when the app is ready to be displayed
    // this is not a provider "really" but keep this at the bottom of the provider tree to allow "lots of react stuff"
    // to run before hiding the home screen to avoid a blank screen between splash and app skeleton load
    HideSplashScreen,
  ];
};

export default useProviders;
